// ============================================================================
// What is being waited on (2026-10-10; lib/waiting.ts is the model): the code
// work in flight grouped by who holds it, each with why and for how long. On
// you comes first, with Prime's cards answerable in place; then what sits with
// Prime, then what is running (agents, checks, merges, builds, a ship). On the
// Now page, docked beside the Graph (a sheet there on a phone), in a sheet from
// Prime's top bar, and behind the chips there.
// ============================================================================
import { useState, type MouseEvent } from 'react'
import { Link } from '@tanstack/react-router'
import { Hourglass, X } from 'lucide-react'
import { useNames } from '@/db/hooks'
import type { Builds } from '@/db/api'
import { useFactoryEnabled } from '@/kinds/agentrun/components'
import { HOLDERS, moving, waited, type Holder, type Waiting } from '@/lib/waiting'
import { Proposal } from '@/views/Chat'
import { useBuilds } from './ShipPipeline'
import { useWaiting } from './RunStatus'
import { IconButton, SectionTitle, Sheet, cx } from './ui'

// the model's hook lives with the one chip every surface draws (RunStatus.tsx)
export { useWaiting }

/** The dot every surface draws for a holder: yours lit, running pulsing, Prime's quiet. */
export const HolderDot = ({ holder, className }: { holder: Holder; className?: string }) => (
  <span
    className={cx(
      'size-2 rounded-full shrink-0',
      holder === 'you' ? 'bg-accent' : moving(holder) ? 'bg-[var(--color-now)] animate-pulse' : 'bg-fg/40',
      className,
    )}
    aria-hidden
  />
)

/** Whether a row's tap was taken in place (the graph chooses the agent's node); false lets the link go. */
type RowPick = (w: Waiting) => boolean

const Row = ({ w, now, onGo, onPick }: { w: Waiting; now: number; onGo?: () => void; onPick?: RowPick }) => {
  const body = (
    <>
      <HolderDot holder={w.holder} className="mt-1.5" />
      <span className="flex-1 min-w-0">
        <span className="block leading-5 line-clamp-2">{w.title}</span>
        <span className="flex items-baseline gap-3 mt-0.5 text-xs">
          <span className="text-muted flex-1 min-w-0 line-clamp-2">{w.why}</span>
          {w.since && <span className="text-faint tabular-nums shrink-0">{waited(w.since, now)}</span>}
        </span>
      </span>
    </>
  )
  const cls = 'flex items-start gap-2.5 px-3 py-2.5 min-h-12'
  const to = w.to
  const tap = (e: MouseEvent) => {
    if (onPick?.(w)) e.preventDefault()
    else onGo?.()
  }
  return (
    <li data-testid="waiting-row" data-holder={w.holder}>
      {'run' in to ? (
        <Link to="/agents/run/$id" params={{ id: to.run }} className={cls} onClick={tap}>
          {body}
        </Link>
      ) : 'review' in to ? (
        <Link to="/dev/review/$id" params={{ id: to.review }} className={cls} onClick={tap}>
          {body}
        </Link>
      ) : 'releases' in to ? (
        <Link to="/dev/releases" className={cls} onClick={tap}>
          {body}
        </Link>
      ) : (
        <Link to="/agents" className={cls} onClick={tap}>
          {body}
        </Link>
      )}
    </li>
  )
}

/**
 * The board: grouped by holder, oldest wait first. Prime's cards on you are
 * the cards themselves (answered here as in the thread); everything else is a
 * row to tap. Nothing when nothing is in flight, unless `empty` says otherwise.
 */
export const WaitingBoard = ({
  builds,
  onGo,
  onPick,
  empty,
}: {
  builds?: Builds | null
  onGo?: () => void
  /** a row's tap, taken in place when it answers true (the Graph chooses the agent's node) */
  onPick?: RowPick
  empty?: string
}) => {
  const factory = useFactoryEnabled()
  const { items, now } = useWaiting(builds)
  const { titleOf } = useNames()
  if (!factory) return null
  if (!items.length)
    return empty ? (
      <p className="text-sm text-muted px-1 py-2" data-testid="waiting-empty">
        {empty}
      </p>
    ) : null
  return (
    <div data-testid="waiting">
      {HOLDERS.map(({ holder, label }) => {
        const mine = items.filter((w) => w.holder === holder)
        if (!mine.length) return null
        // a card is answered in place, the row under it says what it is about
        const cards = holder === 'you' ? mine.filter((w) => w.card) : []
        const rows = mine.filter((w) => !cards.includes(w))
        return (
          <section key={holder} className="mb-4" data-testid={`waiting-${holder}`}>
            <SectionTitle>{label}</SectionTitle>
            {cards.length > 0 && (
              <div className="flex flex-col gap-2 mb-2">
                {cards.map((w) => (
                  <div key={w.key}>
                    {/* every card says whose it is: an agent's merge, or Prime's own ask */}
                    <p
                      className="flex items-baseline gap-3 text-xs text-muted px-1 mb-1"
                      data-testid="waiting-source"
                    >
                      <span className="flex-1 min-w-0 truncate">{w.run ? w.title : 'From Prime'}</span>
                      <span className="text-faint tabular-nums">{waited(w.since, now)}</span>
                    </p>
                    {w.card && <Proposal s={w.card} titleOf={titleOf} acts={false} />}
                  </div>
                ))}
              </div>
            )}
            {rows.length > 0 && (
              <ul className="group-list">
                {rows.map((w) => (
                  <Row key={w.key} w={w} now={now} onGo={onGo} onPick={onPick} />
                ))}
              </ul>
            )}
          </section>
        )
      })}
    </div>
  )
}

/** The board in a sheet, with builds and prod (online), from an hourglass in a top bar. */
export const WaitingButton = () => {
  const [open, setOpen] = useState(false)
  const factory = useFactoryEnabled()
  if (!factory) return null
  return (
    <>
      <IconButton label="What is waiting" onClick={() => setOpen(true)} data-testid="waiting-open">
        <Hourglass size={19} aria-hidden />
      </IconButton>
      {open && <WaitingSheet onClose={() => setOpen(false)} />}
    </>
  )
}

const WaitingSheet = ({ onClose }: { onClose: () => void }) => {
  const { data } = useBuilds()
  return (
    <Sheet open onClose={onClose} title="What is waiting">
      <WaitingBoard builds={data} onGo={onClose} empty="Nothing is in flight." />
    </Sheet>
  )
}

/**
 * The board docked beside the Graph on a laptop (the hub of DevOS, kept open
 * all day): what is on you answered without leaving it. `builds` is the
 * graph's own poll, so the page asks once.
 */
export const WaitingDock = ({
  builds,
  onPick,
  onClose,
}: {
  builds: Builds | null
  onPick?: RowPick
  onClose: () => void
}) => (
  <aside
    className="w-80 shrink-0 card bg-panel p-3 overflow-y-auto fade-in flex flex-col gap-2"
    aria-label="What is waiting"
    data-testid="graph-waiting"
  >
    <div className="flex items-start gap-2">
      <h2 className="flex-1 font-semibold leading-snug">What is waiting</h2>
      <IconButton label="Close" onClick={onClose} data-testid="graph-waiting-close">
        <X size={16} aria-hidden />
      </IconButton>
    </div>
    <WaitingBoard builds={builds} onPick={onPick} empty="Nothing is in flight." />
  </aside>
)
